import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  useUserPreferences,
  useUpdateUserPreferences,
  useProviders,
} from '@/hooks/queries'
import { useAuth } from '@/contexts/AuthContext'

/**
 * Settings — Supply chain & cloud security (Plan C Plan 2 T22).
 *
 * Two boolean toggles plus a chip selector for muting individual providers.
 * Muted providers are excluded from dashboard counts, briefing emails, and
 * critical alerts (enforced backend-side at projection / dispatch time).
 *
 * The chip control is intentionally plain buttons rather than a combobox —
 * the provider list is small (~6 providers as of Plan C launch) and a flat
 * list is more discoverable than a typeahead.
 */
export function SupplyChainSection() {
  const { user } = useAuth()
  const userId = user?.sub ?? ''
  const { data: preferences } = useUserPreferences(userId)
  const updatePreferences = useUpdateUserPreferences()
  const { data: providers = [] } = useProviders()

  const prefs = preferences ?? {
    briefingIncludesSupplyChain: true,
    supplyChainCriticalAlertsEnabled: true,
    mutedProviders: [] as string[],
  }

  const muted = prefs.mutedProviders ?? []

  function toggleProvider(slug: string) {
    const next = muted.includes(slug)
      ? muted.filter((s) => s !== slug)
      : [...muted, slug]
    updatePreferences.mutate({ userId, mutedProviders: next })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Show in daily briefing</p>
          <p className="text-xs text-muted-foreground">
            Supply-chain section in your daily email digest.
          </p>
        </div>
        <Switch
          checked={prefs.briefingIncludesSupplyChain ?? true}
          onCheckedChange={(checked) =>
            updatePreferences.mutate({
              userId,
              briefingIncludesSupplyChain: checked,
            })
          }
          aria-label="Show supply-chain section in daily briefing"
        />
      </div>
      <Separator />
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Critical alerts</p>
          <p className="text-xs text-muted-foreground">
            Security-relevant cloud and identity provider advisories.
          </p>
        </div>
        <Switch
          checked={prefs.supplyChainCriticalAlertsEnabled ?? true}
          onCheckedChange={(checked) =>
            updatePreferences.mutate({
              userId,
              supplyChainCriticalAlertsEnabled: checked,
            })
          }
          aria-label="Enable supply-chain critical alerts"
        />
      </div>
      <Separator />
      <div className="space-y-2">
        <div>
          <p className="text-sm font-medium">Muted providers</p>
          <p className="text-xs text-muted-foreground">
            Excluded from dashboard counts, briefing emails, and critical
            alerts.
          </p>
        </div>
        {providers.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No providers configured.
          </p>
        ) : (
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label="Muted providers"
          >
            {providers.map((p) => {
              const isMuted = muted.includes(p.slug)
              return (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => toggleProvider(p.slug)}
                  aria-pressed={isMuted}
                  className={
                    'rounded-md border px-2.5 py-1 text-xs transition ' +
                    (isMuted
                      ? 'bg-muted text-muted-foreground line-through'
                      : 'hover:bg-muted')
                  }
                >
                  {p.name}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
